import React from "react";
import { Quote, MessageCircleHeart, ArrowRight } from "lucide-react";
import { useLang } from "../LanguageContext";
import { testimonials } from "../data/testimonials";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";

const FEATURE_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_ecole-pleine-nature/artifacts/6sw39336_montessori.webp";

export default function Testimonials() {
  const { t, lang } = useLang();
  const items = testimonials || [];

  return (
    <div data-testid="page-temoignages">
      <section className="container-page pt-16 sm:pt-24 pb-10">
        <p className="eyebrow mb-4">{t("temoignages.eyebrow")}</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-ink max-w-3xl leading-tight">
          {t("temoignages.title")}
        </h1>
        <p className="mt-6 text-lg text-ink-muted leading-relaxed max-w-2xl">
          {t("temoignages.intro")}
        </p>
      </section>

      <section className="container-page pb-10">
        <div className="max-w-xs mx-auto rounded-[2rem] overflow-hidden shadow-soft bg-cream-100">
          <img
            src={FEATURE_IMG}
            alt="École Pleine Nature — un temps de concentration"
            className="w-full h-auto block"
            loading="lazy"
          />
        </div>
      </section>

      <section className="container-page pb-24">
        {items.length === 0 ? (
          <div className="card-soft p-12 sm:p-16 max-w-2xl mx-auto text-center" data-testid="temoignages-empty">
            <div className="w-14 h-14 rounded-2xl bg-forest-light grid place-items-center mx-auto mb-6 text-forest">
              <MessageCircleHeart size={26} strokeWidth={1.6} />
            </div>
            <p className="text-ink-muted leading-relaxed">{t("temoignages.empty")}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {items.map((item) => {
              const quote = lang === "fr" ? item.quote_fr : item.quote_en;
              const author = lang === "fr" ? item.author : item.author_en || item.author;
              const role = lang === "fr" ? item.role_fr : item.role_en;
              const fullTitle = lang === "fr" ? item.full_title_fr : item.full_title_en;
              const paras = lang === "fr" ? item.full_fr : item.full_en;
              return (
                <figure
                  key={item.id}
                  className="card-soft p-8 sm:p-10 flex flex-col"
                  data-testid={`temoignage-${item.id}`}
                >
                  <Quote size={26} className="text-earth mb-5" />
                  <blockquote className="font-heading text-xl sm:text-2xl text-ink leading-snug flex-1">
                    “{quote}”
                  </blockquote>
                  <figcaption className="mt-6">
                    <div className="font-semibold text-ink">{author}</div>
                    {role && <div className="text-sm text-ink-muted mt-1 leading-relaxed">{role}</div>}
                  </figcaption>

                  <Dialog>
                    <DialogTrigger asChild>
                      <button
                        className="btn-outline mt-7 self-start"
                        data-testid={`temoignage-${item.id}-read`}
                      >
                        {t("temoignages.read_full")} <ArrowRight size={18} />
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-cream-50 border-cream-300 rounded-[1.5rem] p-8 sm:p-10">
                      <DialogHeader>
                        <DialogTitle className="font-heading text-2xl sm:text-3xl font-medium text-ink leading-tight text-left">
                          {fullTitle}
                        </DialogTitle>
                        <div className="text-sm text-ink-muted pt-1 text-left">
                          {author}{role ? ` — ${role}` : ""}
                        </div>
                      </DialogHeader>
                      <div className="mt-5 space-y-4 text-ink-muted leading-relaxed text-[15px]">
                        {paras.map((p, i) => (
                          <p key={i}>{p}</p>
                        ))}
                      </div>
                    </DialogContent>
                  </Dialog>
                </figure>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
